import * as THREE from 'three';
import type { World } from '../world/World';
import { lambert } from '../world/Assets';
import { WATER_LEVEL } from '../world/WorldGen';
import { mulberry32, seedAt } from '../core/Rng';

/**
 * BEDO: dünyanın tüccarı.
 * Konumu (seed'e bağlı) doğma noktasının yakınında sabit bir noktadır —
 * oyuncu onu bulabilsin ama hemen üstüne düşmesin.
 * Gündüz tezgâhı açık, gece kapalı (dükkân saatleri veriden gelir).
 */

export interface TraderPost {
  x: number;
  y: number;
  z: number;
  group: THREE.Group;
}

export function createTraderPost(world: World, spawnX: number, spawnZ: number): TraderPost {
  const rand = mulberry32(seedAt(world.params.seed, 1337, 7331, 3));
  // Doğma noktasının 30-55 birim uzağında karada bir yer bul
  let x = spawnX + 34;
  let z = spawnZ + 12;
  for (let i = 0; i < 200; i++) {
    const a = rand() * Math.PI * 2;
    const r = 30 + rand() * 25;
    const cx = spawnX + Math.cos(a) * r;
    const cz = spawnZ + Math.sin(a) * r;
    const h = world.heightAt(cx, cz);
    if (h > WATER_LEVEL + 1.2 && world.slopeAt(cx, cz) < 0.45) {
      x = cx;
      z = cz;
      break;
    }
  }
  const y = world.heightAt(x, z);

  const group = new THREE.Group();
  group.position.set(x, y, z);

  const skin = lambert(0xffccbc);
  const cloth = lambert(0xff9800);

  // Gövde
  const body = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.9, 0.45), cloth);
  body.position.y = 1.05;
  group.add(body);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.42, 0.42), skin);
  head.position.y = 1.72;
  group.add(head);
  // Altın diş (BEDO'nun imzası)
  const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.07, 0.05), lambert(0xffea00));
  tooth.position.set(0.07, 1.6, 0.22);
  group.add(tooth);
  const hat = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.16, 0.5), lambert(0x3e2723));
  hat.position.y = 1.98;
  group.add(hat);
  for (const sx of [-0.46, 0.46]) {
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.8, 0.18), skin);
    arm.position.set(sx, 1.05, 0);
    group.add(arm);
  }
  for (const sx of [-0.18, 0.18]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.62, 0.22), lambert(0x2196f3));
    leg.position.set(sx, 0.31, 0);
    group.add(leg);
  }

  // Tezgâh
  const stall = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.18, 1.1), lambert(0x8d6e46));
  stall.position.set(0, 0.95, 1.1);
  group.add(stall);
  for (const [lx, lz] of [
    [-1.15, 0.65],
    [1.15, 0.65],
    [-1.15, 1.55],
    [1.15, 1.55],
  ]) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.9, 0.14), lambert(0x5d4a30));
    leg.position.set(lx, 0.45, lz);
    group.add(leg);
  }
  // Tente
  const canopy = new THREE.Mesh(new THREE.BoxGeometry(3, 0.12, 1.6), lambert(0xd84315));
  canopy.position.set(0, 2.1, 1.1);
  group.add(canopy);
  for (const lx of [-1.4, 1.4]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.12, 2.1, 0.12), lambert(0x5d4a30));
    post.position.set(lx, 1.05, 1.65);
    group.add(post);
  }

  group.traverse((o) => {
    if (o instanceof THREE.Mesh) o.castShadow = false;
  });
  world.scene.add(group);
  return { x, y, z, group };
}

/** BEDO'nun dükkânı bu saatte açık mı? */
export function shopOpen(hour: number): boolean {
  return hour >= 8 && hour < 20;
}
